import type { ReactElement } from 'react';
import { EmberDisc } from '../components/EmberDisc';
import { EmberMascot } from '../components/EmberMascot';
import './ConnectionShots.css';
import './WhyShots.css';

/**
 * The pictures on the homepage's second screen, What's different here
 * (2026-09-13 拍板, evening). Each difference is shown as the real thing the
 * reader will meet in the product, drawn from the same anatomy as the How it
 * works pictures (ConnectionShots: the thread's bubbles, the page a
 * conversation leaves, the primary button) — but without the frame around
 * them. The How it works pictures show where a step happens, so they are
 * windows; these show what a voice says, so the bubbles and the page sit on
 * the paper itself, at a size where the words can be read. The words are one
 * made-up telling, the one How it works also uses (aria-hidden; the copy
 * under each picture carries the difference). Ember stands beside what is
 * Ember's own — its bubbles, on the thread's paper disc — and by the Mattered
 * moment, touched; never on the person's own page (2026-09-14).
 */
export function WhyShot({ shot }: { shot: string }): ReactElement | null {
  switch (shot) {
    case 'informed': return <InformedShot />;
    case 'clearer': return <ClearerShot />;
    case 'kept': return <KeptShot />;
    case 'mattered': return <MatteredShot />;
    default: return null;
  }
}

/* 1 · Ember's reply, over a fan of the pages others have told here. */
function InformedShot(): ReactElement {
  return (
    <div className="ws-shot ws-informed" aria-hidden="true">
      <span className="ws-fan"><i /><i /><i /><i /><i /></span>
      <div className="ws-turn"><EmberDisc face="warm" /><p className="cs-bubble ws-bubble">People who’ve stood here often say the hardest part wasn’t the leaving. Was it, for you?</p></div>
    </div>
  );
}

/* 2 · the person's own words, one question, and the second line clearer
   than the first. */
function ClearerShot(): ReactElement {
  return (
    <div className="ws-shot ws-clearer" aria-hidden="true">
      <p className="cs-bubble cs-bubble-you ws-bubble">I left the job I spent ten years building. Everyone says I was brave. I don’t feel brave.</p>
      <div className="ws-turn"><EmberDisc face="curious" /><p className="cs-bubble ws-bubble">Brave is other people’s word. What’s yours?</p></div>
      <p className="cs-bubble cs-bubble-you ws-bubble">Relieved. And ashamed of being relieved.</p>
    </div>
  );
}

/* 3 · the page a conversation leaves behind — the same page How it works
   shows in its second step. */
function KeptShot(): ReactElement {
  return (
    <div className="ws-shot ws-kept" aria-hidden="true">
      <div className="cs-ember-page ws-page">
        <div className="cs-ember-art">
          <i className="cs-art-hill" /><i className="cs-art-sun" /><i className="cs-art-far" /><i className="cs-art-door" />
        </div>
        <div className="cs-ember-body">
          <span className="cs-ember-meta">Told in writing · September 6, 2026</span>
          <span className="cs-ember-title">Ten years, and the door</span>
          <p>The morning I handed in my badge, the lobby smelled the same as it had on my first day. I stood there longer than I needed to. Nobody had asked me to stay, and nobody had asked me to go — that was the part I couldn’t explain to anyone at</p>
        </div>
      </div>
    </div>
  );
}

/* 4 · another person's page, opened after both said yes, and the word sent
   back (the Mattered exchange, §4.9). A different telling from the reader's
   own page above, so the two are not read as one. */
function MatteredShot(): ReactElement {
  return (
    <div className="ws-shot ws-mattered" aria-hidden="true">
      <span className="ws-stamp">They said yes.</span>
      {/* Ember, touched: this is the Mattered moment — the one picture on the
          page where an extended face is right (2026-09-14). */}
      <span className="ws-ember-figure"><EmberMascot face="touched" still interactive={false} className="ws-ember-mascot" /></span>
      <div className="cs-ember-page ws-page">
        <div className="cs-ember-art ws-art-theirs">
          <i className="cs-art-hill" /><i className="cs-art-sun" /><i className="cs-art-far" />
        </div>
        <div className="cs-ember-body">
          <span className="cs-ember-meta">Told by another person</span>
          <span className="cs-ember-title">The badge in the drawer</span>
          <p>I kept the badge in a drawer for a year. Not because I missed the place. Because throwing it out would have meant admitting I had chosen this, and nobody had made me.</p>
        </div>
      </div>
      <span className="cs-btn cs-btn-primary ws-mattered-word">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12l5 5L20 7" /></svg>
        This mattered
      </span>
    </div>
  );
}
